// src/screens/Home.jsx
import React, { useCallback } from "react";
import NewsSection from "../components/news/NewsSection";
import MarketTape from "../components/news/MarketTape";
import { apiFetch } from "../api";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import PullToRefreshHeader from "../components/PullToRefreshHeader";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

async function fetchTape() {
  const r = await apiFetch("/api/home/market-tape");
  return Array.isArray(r?.items) ? r.items : [];
}

async function fetchBucket(bucket, limit = 5) {
  const r = await apiFetch(`/api/home/news?bucket=${encodeURIComponent(bucket)}&limit=${limit}`);
  return Array.isArray(r?.items) ? r.items : [];
}

export default function Home() {
  const queryClient = useQueryClient();

  // =========================
  // ✅ Queries
  // =========================
  const tapeQuery = useQuery({
    queryKey: ["home", "marketTape"],
    queryFn: fetchTape,
    staleTime: 30_000,
    gcTime: 10 * 60_000,
  });

  const marketsQuery = useQuery({
    queryKey: ["home", "news", "br_markets", 5],
    queryFn: () => fetchBucket("br_markets", 5),
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
  });

  const businessQuery = useQuery({
    queryKey: ["home", "news", "br_business", 5],
    queryFn: () => fetchBucket("br_business", 5),
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
  });

  const macroQuery = useQuery({
    queryKey: ["home", "news", "br_macro", 5],
    queryFn: () => fetchBucket("br_macro", 5),
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
  });

  // ✅ NOVOS BUCKETS
  const techQuery = useQuery({
    queryKey: ["home", "news", "br_tech", 5],
    queryFn: () => fetchBucket("br_tech", 5),
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
  });

  const startupsQuery = useQuery({
    queryKey: ["home", "news", "br_startups", 5],
    queryFn: () => fetchBucket("br_startups", 5),
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
  });

  // =========================
  // ✅ Pull-to-refresh (perene)
  // =========================
  const refreshAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["home"] });
    await queryClient.refetchQueries({ queryKey: ["home"], type: "active" });
  }, [queryClient]);

  const ptr = usePullToRefresh({
    enabled: true,
    isBlocked: false,
    startZoneY: 260,
    maxPull: 110,
    readyPull: 70,
    refreshHeight: 56,
    onRefresh: refreshAll,
  });

  // =========================
  // ✅ Data -> UI props
  // =========================
  const tapeItems = tapeQuery.data ?? [];
  const loadingTape = tapeQuery.isLoading;
  const errorTape = tapeQuery.error?.message || null;

  const markets = marketsQuery.data ?? [];
  const business = businessQuery.data ?? [];
  const macro = macroQuery.data ?? [];
  const tech = techQuery.data ?? [];
  const startups = startupsQuery.data ?? [];

  const loadingMarkets = marketsQuery.isLoading;
  const loadingBusiness = businessQuery.isLoading;
  const loadingMacro = macroQuery.isLoading;
  const loadingTech = techQuery.isLoading;
  const loadingStartups = startupsQuery.isLoading;

  const errorMarkets = marketsQuery.error?.message || null;
  const errorBusiness = businessQuery.error?.message || null;
  const errorMacro = macroQuery.error?.message || null;
  const errorTech = techQuery.error?.message || null;
  const errorStartups = startupsQuery.error?.message || null;

  return (
    <div
      className="screen home"
      onTouchStart={ptr.handlers.onTouchStart}
      onTouchMove={ptr.handlers.onTouchMove}
      onTouchEnd={ptr.handlers.onTouchEnd}
      onTouchCancel={ptr.handlers.onTouchEnd}
    >
      <PullToRefreshHeader height={ptr.height} refreshing={ptr.refreshing} ready={ptr.ready} />

      <MarketTape items={tapeItems} loading={loadingTape} error={errorTape} />

      <NewsSection
        title="Mercados (Brasil)"
        items={markets}
        loading={loadingMarkets}
        error={errorMarkets}
        skeletonCount={3}
        emptyText="Sem notícias de mercado agora."
      />

      <NewsSection
        title="Negócios (Brasil)"
        items={business}
        loading={loadingBusiness}
        error={errorBusiness}
        skeletonCount={3}
        emptyText="Sem notícias de negócios agora."
      />

      <NewsSection
        title="Macro & Economia"
        items={macro}
        loading={loadingMacro}
        error={errorMacro}
        skeletonCount={3}
        emptyText="Sem notícias de macro agora."
      />

      {/* ✅ NOVAS SEÇÕES */}
      <NewsSection
        title="Tech (Brasil)"
        items={tech}
        loading={loadingTech}
        error={errorTech}
        skeletonCount={3}
        emptyText="Sem notícias de tecnologia agora."
      />

      <NewsSection
        title="Startups & VC (Brasil)"
        items={startups}
        loading={loadingStartups}
        error={errorStartups}
        skeletonCount={3}
        emptyText="Sem notícias de startups agora."
      />
    </div>
  );
}
